/**
 * ConsentGate — ecosystem-wide hard-gate for the data-processing consent.
 *
 * Mounts at the App shell. Any authenticated user (student / teacher / admin /
 * member / delegate) sees a blocking full-screen dialog with the active
 * policy text and a checkbox until they sign. The signature is recorded
 * server-side (POST /api/consent/accept) which captures user_id + tenant_id +
 * policy_version + IP + UA + timestamp.
 *
 * If the admin bumps the policy version, this gate reappears on the next
 * page load — same component, same flow.
 *
 * The gate is intentionally NOT modal-on-top — it replaces the page content
 * so there's no "X" or escape route. The legal contract is "you cannot use
 * the system until you agree".
 *
 * Bypass logic:
 *   - Public routes (login / register / public CMS pages) are excluded by
 *     the wrapping component (we only mount inside RequireAuth boundaries).
 *   - Anonymous calls to /api/consent/status return 401 — we treat that as
 *     "no gate needed, user is on a public page".
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ShieldCheck, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { getToken } from '@/lib/authToken';

export default function ConsentGate({ children }) {
  const [agreed, setAgreed] = useState(false);
  // Only fire the consent check once we KNOW the user is authenticated.
  // Without this guard the api response interceptor sees an anonymous 401
  // and forces a logout/redirect — which would loop the user off the
  // dashboard the moment they sign in.
  const storeToken = useAuthStore((s) => s.token);
  const isAuthenticated = !!(storeToken || getToken());

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['consent-status'],
    // Always settles: a request left hanging by a phone that backgrounded
    // itself mid-login used to leave the whole app behind a spinner.
    queryFn: () => Promise.race([
      api.get('/consent/status').then(r => r.data),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('consent check timed out')), 8000)),
    ]),
    enabled: isAuthenticated,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: 1,
  });

  // Did this user ever sign ANY policy version before? When yes, a version
  // bump shows the small banner instead of the full-page gate. The per-user
  // dismissal lives in localStorage so it survives reloads — server still
  // tracks the formal signature, this is purely UX.
  const hasSignedAny = !!data?.signed_at || !!data?.ever_signed;
  const lsDismissKey = `consent-banner-dismissed:${data?.user_id || ''}:${data?.policy?.version || ''}`;
  const [bannerDismissed, setBannerDismissed] = useState(false);
  useEffect(() => {
    try { setBannerDismissed(localStorage.getItem(lsDismissKey) === '1'); } catch {}
  }, [lsDismissKey]);

  const acceptMut = useMutation({
    mutationFn: () => api.post('/consent/accept', {
      policy_version: data?.policy?.version,
      accepted: true,
    }).then(r => r.data),
    onSuccess: () => { toast.success('Thank you — your consent has been recorded.'); refetch(); },
    onError: (e) => toast.error(getErrorMessage(e) || 'Could not record consent — please try again.'),
  });

  // Not authenticated, still checking, or consent already on file → render the
  // app. The check must never hold the whole app behind a spinner: on a phone
  // that request can stall, and the result was a screen nobody could scroll or
  // tap. The gate appears only once we positively know consent is needed.
  if (isLoading || !isAuthenticated || !data?.needs_consent) {
    return children;
  }

  const acceptInline = () => {
    try { localStorage.setItem(lsDismissKey, '1'); } catch {}
    setBannerDismissed(true);
    acceptMut.mutate();
  };

  // User has signed before AND just needs to re-acknowledge a version bump —
  // surface a small banner above the app shell instead of the blocking gate.
  // Per the user's directive: "one time when they gain access, then a smaller
  // banner thereafter."
  if (hasSignedAny && !bannerDismissed) {
    return (
      <>
        <div className="bg-fuchsia-50 border-b border-fuchsia-200 px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="consent-banner">
          <ShieldCheck className="h-4 w-4 text-fuchsia-600 shrink-0" />
          <p className="text-xs text-slate-700 flex-1 min-w-0 sm:min-w-[200px]">
            Our data-processing policy was updated to <span className="font-mono">{data.policy.version}</span>.
            Please re-acknowledge to continue.
          </p>
          <Button size="sm" onClick={acceptInline} disabled={acceptMut.isPending} className="h-7 text-xs" data-testid="consent-banner-accept">
            {acceptMut.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            I agree to v{data.policy.version}
          </Button>
        </div>
        {children}
      </>
    );
  }

  // First-ever signature — full-page hard gate.

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-fuchsia-50 via-white to-violet-50 p-4" data-testid="consent-gate">
      <Card className="max-w-2xl w-full shadow-xl border-fuchsia-100">
        <CardContent className="p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-fuchsia-100 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-fuchsia-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Before you continue</h1>
              <p className="text-sm text-slate-500">We need your consent to process your data.</p>
            </div>
          </div>

          <div className="border-l-4 border-fuchsia-300 bg-fuchsia-50/50 rounded-r-md p-4 text-sm text-slate-700 leading-relaxed" data-testid="consent-policy-text">
            <p className="flex items-start gap-2">
              <FileText className="h-4 w-4 mt-0.5 text-fuchsia-500 shrink-0" />
              <span>{data.policy.text}</span>
            </p>
            <p className="text-xs text-slate-500 mt-2 pl-6">
              Policy version <span className="font-mono">{data.policy.version}</span>
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md bg-slate-50 hover:bg-slate-100 transition">
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              className="mt-0.5"
              data-testid="consent-agree-checkbox"
            />
            <span className="text-sm text-slate-700">
              I have read and agree to the statement above. I understand that my consent will be
              recorded with my account, including the time and origin of this signature, and that
              this record will be kept on file as evidence of my agreement.
            </span>
          </label>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              disabled={!agreed || acceptMut.isPending}
              onClick={() => acceptMut.mutate()}
              className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700"
              data-testid="consent-accept-btn"
            >
              {acceptMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              I agree and continue
            </Button>
          </div>

          <p className="text-xs text-slate-400 text-center">
            Need to withdraw consent later? Contact your school administrator.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
